import { Injectable, OnDestroy } from '@angular/core';
import { NavigationService } from '@frk/eds-components';
import { IUserProfile, UserRole } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { SegmentService } from '@services/segment.service';
import { SiteConfigService } from '@services/site-config.service';
import {
  LiteratureConfig,
  LiteratureListingConfiguration,
  LiteraturePageLayoutConfig,
  SegmentId,
} from '@types';
import { SH } from '@utils/app.constants';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
interface LiteratureUrls {
  litDetailUrl: string;
  litOrderCheckoutUrl: string;
  litOrderHistoryDetailUrl: string;
  orderConfirm: string;
  orderHistoryList: string;
  orderSuccess: string;
  orderCheckoutAddress: string;
  litListing: string;
  myLiteratureLandingUrl: string;
}
@Injectable({ providedIn: 'root' })
export class LiteratureGlobalConfigService implements OnDestroy {
  public navigationUrls: LiteratureUrls;
  public isInternational: boolean;
  public dateFormat: string;
  public literatureConfig: LiteratureConfig;
  private literaturePageLayoutConfig: LiteraturePageLayoutConfig;
  private documentMappingConfiguration: Map<string, string>;
  private userProfile: IUserProfile;
  unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private siteConfig: SiteConfigService,
    private navService: NavigationService,
    private profileService: ProfileService,
    private segmentService: SegmentService
  ) {
    this.siteConfig.getIsPopulated$().subscribe((isPopulate: boolean) => {
      if (isPopulate) {
        this.literatureConfig = this.siteConfig.literature;
        this.loadLiteratureConfiguration();
      }
    });

    this.profileService
      .getUserProfile$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile: IUserProfile): boolean => {
          if (profile.isLoggedIn) {
            return !!profile?.profileInfo?.userId;
          } else {
            return true;
          }
        })
      )
      .subscribe((data: IUserProfile) => {
        this.userProfile = data;
      });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private loadLiteratureConfiguration(): void {
    this.navigationUrls = {
      litDetailUrl: this.getSpaBaseUrl(this.literatureConfig?.litDetailUrl),
      litListing: this.getSpaBaseUrl(this.siteConfig.literature?.litListing),
      litOrderCheckoutUrl: this.getSpaBaseUrl(
        this.literatureConfig?.litOrderCheckoutUrl
      ),
      litOrderHistoryDetailUrl: this.getSpaBaseUrl(
        this.literatureConfig?.litOrderHistoryDetailUrl
      ),
      orderCheckoutAddress: this.getSpaBaseUrl(
        this.literatureConfig?.orderCheckoutAddress
      ),
      orderConfirm: this.getSpaBaseUrl(this.literatureConfig?.orderConfirm),
      orderSuccess: this.getSpaBaseUrl(this.literatureConfig?.orderSuccess),
      orderHistoryList: this.getSpaBaseUrl(
        this.literatureConfig?.orderHistoryList
      ),
      myLiteratureLandingUrl: this.getSpaBaseUrl(
        this.literatureConfig?.myLiteratureLandingUrl
      ),
    };
    this.isInternational = this.siteConfig.isSiteInternational();
    this.dateFormat = this.siteConfig.common?.dateFormat;
    this.documentMappingConfiguration = new Map<string, string>();
    if (this.literatureConfig.criteriaType) {
      this.literatureConfig.criteriaType.forEach((criteria) => {
        criteria.documentTypeDisplay?.forEach((docType) => {
          this.documentMappingConfiguration.set(
            docType,
            criteria.criteriaTypeName
          );
        });
      });
    }
  }

  private getSpaBaseUrl(url: string) {
    const apiUrls = this.siteConfig.apiUrls;
    let link = `/${url}`;
    if (apiUrls && apiUrls.spaBaseUrl) {
      link = `${apiUrls.spaBaseUrl}${link}`;
    }
    return link;
  }

  public getDocumentMappingConfiguration(): Map<string, string> {
    return this.documentMappingConfiguration;
  }

  public navigateToUrl(url: string): void {
    this.navService.navigateByUrl(url);
  }

  public getUserProfile(): IUserProfile {
    return this.userProfile;
  }

  IsUserLoggedInNotShareHolder(): boolean {
    return (
      this.userProfile?.isLoggedIn &&
      UserRole[this.userProfile?.profileInfo?.role] !== SH.toLocaleLowerCase()
    );
  }

  public getLiteratureListingConfig(): LiteratureListingConfiguration {
    if (this.literaturePageLayoutConfig) {
      return this.literatureConfig?.pageType?.find(
        (item: LiteratureListingConfiguration) =>
          item.pageTypeName === this.literaturePageLayoutConfig.pageType
      );
    }
    return;
  }

  public setLiteratureListingConfig(
    literaturePageLayoutConfig: LiteraturePageLayoutConfig
  ) {
    this.literaturePageLayoutConfig = literaturePageLayoutConfig;
  }

  public getSegmentId(): SegmentId {
    return this.segmentService.getCurrentSegmentId();
  }
}
